import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Space,
  Typography,
  Upload,
  Divider,
  Alert,
  Tag,
  Row,
  Col,
  Tooltip
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  DownloadOutlined,
  ReloadOutlined,
  FileExcelOutlined,
  BookOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile } from 'antd/es/upload/interface';
import * as XLSX from 'xlsx';
import { NganhHoc, CreateNganhHocRequest, UpdateNganhHocRequest, ImportNganhHocData } from '../../../../shared/types';
import nganhHocService from '../../services/nganhHocService';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface NganhHocFormData {
  NganhHocID: string;
  TenNganh: string;
  MoTa?: string;
}

const NganhHocManagementPage: React.FC = () => {
  // State management
  const [nganhHoc, setNganhHoc] = useState<NganhHoc[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingNganhHoc, setEditingNganhHoc] = useState<NganhHoc | null>(null);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  
  // Pagination
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  // Search
  const [searchText, setSearchText] = useState('');

  const [form] = Form.useForm<NganhHocFormData>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load data
  useEffect(() => {
    fetchNganhHoc();
  }, [pagination.current, pagination.pageSize, searchText]);

  const fetchNganhHoc = async () => {
    setLoading(true);
    try {
      const response = await nganhHocService.getNganhHoc({
        page: pagination.current,
        limit: pagination.pageSize,
        search: searchText
      });

      if (response.success) {
        setNganhHoc(response.data);
        setPagination(prev => ({
          ...prev,
          total: response.pagination.total
        }));
      }
    } catch (error) {
      console.error('Error fetching nganh hoc:', error);
      message.error('Không thể tải danh sách ngành học');
    } finally {
      setLoading(false);
    }
  };

  // Handle table pagination change
  const handleTableChange = (page: number, pageSize?: number) => {
    setPagination(prev => ({
      ...prev,
      current: page,
      pageSize: pageSize || prev.pageSize
    }));
  };

  // Handle search
  const handleSearch = (value: string) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Handle add new
  const handleAdd = () => {
    setEditingNganhHoc(null);
    form.resetFields();
    setModalVisible(true);
  };

  // Handle edit
  const handleEdit = (record: NganhHoc) => {
    setEditingNganhHoc(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  // Handle delete
  const handleDelete = async (id: string) => {
    try {
      const response = await nganhHocService.deleteNganhHoc(id);
      if (response.success) {
        message.success('Xóa ngành học thành công');
        fetchNganhHoc();
      }
    } catch (error) {
      console.error('Error deleting nganh hoc:', error);
      message.error('Không thể xóa ngành học');
    }
  };

  // Handle form submit
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      let response;
      if (editingNganhHoc) {
        response = await nganhHocService.updateNganhHoc(editingNganhHoc.NganhHocID, values);
      } else {
        response = await nganhHocService.createNganhHoc(values);
      }

      if (response.success) {
        message.success(editingNganhHoc ? 'Cập nhật ngành học thành công' : 'Tạo ngành học thành công');
        setModalVisible(false);
        fetchNganhHoc();
      }
    } catch (error) {
      console.error('Error saving nganh hoc:', error);
      message.error('Không thể lưu ngành học');
    } finally {
      setLoading(false);
    }
  };

  // Handle import Excel
  const handleImportExcel = () => {
    setImportModalVisible(true);
    setFileList([]);
  };

  // Handle file upload
  const handleFileUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];

        // Skip header row and map data
        const items: ImportNganhHocData[] = jsonData
          .slice(1)
          .map(row => ({
            NganhHocID: row[0]?.toString() || '',
            TenNganh: row[1]?.toString() || '',
            MoTa: row[2]?.toString() || ''
          }))
          .filter(item => item.NganhHocID && item.TenNganh);

        if (items.length === 0) {
          message.error('File không có dữ liệu hợp lệ');
          return;
        }

        setImportLoading(true);
        const response = await nganhHocService.importNganhHoc(items);

        if (response.success) {
          const { inserted, errors } = response.data;
          if (inserted.length > 0) {
            message.success(`Import thành công ${inserted.length} ngành học`);
          }
          if (errors.length > 0) {
            message.warning(`${errors.length} dòng bị lỗi khi import`);
            console.warn('Import errors:', errors);
          }
          setImportModalVisible(false);
          fetchNganhHoc();
        }
      } catch (error) {
        console.error('Error importing file:', error);
        message.error('Lỗi khi đọc file Excel');
      } finally {
        setImportLoading(false);
      }
    };
    reader.readAsArrayBuffer(file);
    return false; // Prevent upload
  };

  // Download template
  const handleDownloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([
      ['Mã ngành', 'Tên ngành', 'Mô tả'],
      ['176', 'Cao đẳng Y sĩ da khoa', 'Ngành đào tạo y sĩ đa khoa'],
      ['178', 'Cao đẳng Dược', 'Ngành đào tạo dược sĩ'],
      ['179', 'Cao đẳng Điều dưỡng', 'Ngành đào tạo điều dưỡng viên']
    ]);
    XLSX.utils.book_append_sheet(wb, ws, 'NganhHoc');
    XLSX.writeFile(wb, 'template_import_nganhhoc.xlsx');
  };

  // Table columns
  const columns: ColumnsType<NganhHoc> = [
    {
      title: 'Mã ngành',
      dataIndex: 'NganhHocID',
      key: 'NganhHocID',
      width: 120,
      render: (text: string) => <Tag color="blue">{text}</Tag>
    },
    {
      title: 'Tên ngành',
      dataIndex: 'TenNganh',
      key: 'TenNganh',
      ellipsis: true
    },
    {
      title: 'Mô tả',
      dataIndex: 'MoTa',
      key: 'MoTa',
      ellipsis: true,
      render: (text: string) => text || <Text type="secondary">Chưa có mô tả</Text>
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title="Sửa">
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Xóa ngành học"
            description="Bạn có chắc chắn muốn xóa ngành học này?"
            onConfirm={() => handleDelete(record.NganhHocID)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Tooltip title="Xóa">
              <Button
                type="primary"
                danger
                size="small"
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <BookOutlined />
            <Title level={4} style={{ margin: 0 }}>
              Quản lý Ngành học
            </Title>
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchNganhHoc}
              loading={loading}
            >
              Làm mới
            </Button>
          </Space>
        }
      >
        {/* Action Bar */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col flex="auto">
            <Input.Search
              placeholder="Tìm kiếm theo mã ngành hoặc tên ngành..."
              allowClear
              onSearch={handleSearch}
              style={{ maxWidth: 400 }}
            />
          </Col>
          <Col>
            <Space>
              <Button
                type="default"
                icon={<DownloadOutlined />}
                onClick={handleDownloadTemplate}
              >
                Tải template
              </Button>
              <Button
                type="default"
                icon={<UploadOutlined />}
                onClick={handleImportExcel}
              >
                Import Excel
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
              >
                Thêm ngành học
              </Button>
            </Space>
          </Col>
        </Row>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={nganhHoc}
          rowKey="NganhHocID"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} của ${total} ngành học`,
            onChange: handleTableChange,
            onShowSizeChange: handleTableChange
          }}
        />
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={editingNganhHoc ? 'Cập nhật ngành học' : 'Thêm ngành học mới'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        confirmLoading={loading}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            NganhHocID: '',
            TenNganh: '',
            MoTa: ''
          }}
        >
          <Form.Item
            label="Mã ngành"
            name="NganhHocID"
            rules={[
              { required: true, message: 'Vui lòng nhập mã ngành' },
              { max: 10, message: 'Mã ngành không được quá 10 ký tự' }
            ]}
          >
            <Input
              placeholder="Ví dụ: 176, 178, 179..."
              disabled={!!editingNganhHoc}
            />
          </Form.Item>

          <Form.Item
            label="Tên ngành"
            name="TenNganh"
            rules={[
              { required: true, message: 'Vui lòng nhập tên ngành' },
              { max: 255, message: 'Tên ngành không được quá 255 ký tự' }
            ]}
          >
            <Input placeholder="Ví dụ: Cao đẳng Y sĩ da khoa" />
          </Form.Item>

          <Form.Item
            label="Mô tả"
            name="MoTa"
            rules={[
              { max: 500, message: 'Mô tả không được quá 500 ký tự' }
            ]}
          >
            <TextArea
              rows={3}
              placeholder="Mô tả chi tiết về ngành học..."
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Import Modal */}
      <Modal
        title="Import ngành học từ Excel"
        open={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        footer={null}
        width={600}
      >
        <Alert
          message="Hướng dẫn import"
          description={
            <div>
              <p>1. Tải file template và điền thông tin theo mẫu</p>
              <p>2. Cột 1: Mã ngành (bắt buộc)</p>
              <p>3. Cột 2: Tên ngành (bắt buộc)</p>
              <p>4. Cột 3: Mô tả (tùy chọn)</p>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Space direction="vertical" style={{ width: '100%' }}>
          <Button
            icon={<FileExcelOutlined />}
            onClick={handleDownloadTemplate}
            block
          >
            Tải file template Excel
          </Button>

          <Divider>Hoặc</Divider>

          <Upload
            accept=".xlsx,.xls"
            fileList={fileList}
            beforeUpload={handleFileUpload}
            onChange={({ fileList }) => setFileList(fileList)}
            maxCount={1}
          >
            <Button
              icon={<UploadOutlined />}
              loading={importLoading}
              block
            >
              Chọn file Excel để import
            </Button>
          </Upload>
        </Space>
      </Modal>
    </div>
  );
};

export default NganhHocManagementPage; 